import { useState } from "react";
import { Box, Typography, Tabs, Tab } from "@mui/material";
import { useDispatch } from "react-redux";
import ViewPdf from "./pdf_annotator/ViewPdf";

const AnnotatedProofBody = ({
  proof_data,
  onApprove,
  onReject,
  proofLoading,
  onRefresh,
  onAddComment,
}) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);

  if (activeTab >= proof_data?.length) {
    setActiveTab(0);
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getTabLabel = (index) => {
    // 0 -> Proof Option A, 1 -> Proof Option B, etc.
    const letter = String.fromCharCode(65 + index);
    return `Proof Option ${letter}`;
  };

  if (!Array.isArray(proof_data) || proof_data.length === 0) {
    return (
      <Box sx={{ my: 2, minHeight: 200 }}>
        <Typography variant="body2" color="text.secondary">
          No proof data available.
        </Typography>
      </Box>
    );
  }

  const hasMultiple = proof_data.length > 1;

  const isAnyApproved = proof_data?.some(
    (item) => item.sent_approved_files === 2,
  );
  const allRejected = proof_data?.every(
    (item) => item.sent_approved_files === 3,
  );

  return (
    <Box sx={{ my: 2, minHeight: 200 }}>
      <Box
        sx={{
          backgroundColor: "#fff",
          boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
          mb: 0,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold" }}
            gutterBottom
          >
            Instructions:
          </Typography>
          <Typography variant="body1" paragraph sx={{ m: 0, fontSize: "14px" }}>
            1. Click on the PDF and select text or image or any blank area.
          </Typography>
          <Typography variant="body1" paragraph sx={{ m: 0, fontSize: "14px" }}>
            2. Enter comment and click on the save changes to save the comment.
          </Typography>
          <Typography variant="body1" paragraph sx={{ m: 0, fontSize: "14px" }}>
            3. Mouse over on the highlighted area to see the Edit or Delete
            comment option.
          </Typography>
          <Typography variant="body1" paragraph sx={{ m: 0, fontSize: "14px" }}>
            4. Click on the "Approve" Button to approve the Proof without
            comments. You cannot Add or Edit any comments after you click on the
            "Approve".
          </Typography>
          <Typography variant="body1" paragraph sx={{ m: 0, fontSize: "14px" }}>
            5. If you have added all comments; Click on the "Need Changes". You
            cannot Add or Edit any comments after you click on the "Need
            Changes".
          </Typography>
        </Box>

        {hasMultiple && (
          <Box
            sx={{
              borderTop: 1,
              borderColor: "divider",
              backgroundColor: "#fff",
            }}
          >
            <Typography
              variant="body2"
              sx={{ py: 1, px: 2, color: "#666", fontWeight: "bold" }}
            >
              Please review all the Proof option(s) before Approving.
            </Typography>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="proof tabs"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2 }}
              TabIndicatorProps={{
                style: {
                  backgroundColor: (() => {
                    const activeItem = proof_data[activeTab];
                    if (
                      activeItem?.sent_approved_files === 2 ||
                      activeItem?.proof_status === "2"
                    ) {
                      return "#2e7d32";
                    }
                    if (
                      activeItem?.sent_approved_files === 3 ||
                      activeItem?.proof_status === "3"
                    ) {
                      return "#c62828";
                    }
                    return "#1976d2";
                  })(),
                },
              }}
            >
              {proof_data.map((item, index) => {
                let tabStyle = {
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd", // Light blue for selected default
                  },
                };
                if (
                  item.sent_approved_files === 2 ||
                  item.proof_status === "2"
                ) {
                  tabStyle = {
                    // backgroundColor: "#e8f5e9", // Light green
                    color: "#1b5e20",
                    fontWeight: "bold",
                    "&.Mui-selected": {
                      color: "#1b5e20",
                      backgroundColor: "#e8f5e9", // Light Green for selected approved
                    },
                  };
                } else if (
                  item.sent_approved_files === 3 ||
                  item.proof_status === "3"
                ) {
                  tabStyle = {
                    // backgroundColor: "#ffebee", // Light red
                    color: "#c62828",
                    fontWeight: "bold",
                    "&.Mui-selected": {
                      color: "#c62828",
                      backgroundColor: "#ffebee", // Light Red for selected rejected
                    },
                  };
                }

                return (
                  <Tab key={item.id} label={getTabLabel(index)} sx={tabStyle} />
                );
              })}
            </Tabs>
          </Box>
        )}
      </Box>

      {proof_data.map((item, index) => (
        <div
          role="tabpanel"
          hidden={activeTab !== index}
          id={`proof-tabpanel-${index}`}
          aria-labelledby={`proof-tab-${index}`}
          key={item.id}
        >
          {activeTab === index && (
            <Box sx={{ p: 0, mt: 0 }}>
              <ViewPdf
                file_url={item.url}
                proof_id={item.id}
                initial_comments={item.proof_comments}
                proof_status={item.proof_status}
                is_view={isAnyApproved || allRejected}
                onApprove={onApprove}
                onReject={onReject}
                onRefresh={onRefresh}
                onAddComment={onAddComment}
                tabLabel={hasMultiple ? getTabLabel(index) : null}
              />
            </Box>
          )}
        </div>
      ))}
    </Box>
  );
};

export default AnnotatedProofBody;
